import { Button, Text } from "@radix-ui/themes";
import jwt from "jsonwebtoken";
import { useTranslations } from "next-intl";
import { fetchHost, fetchUtil } from "@/util/fetchutil";
import { getApiHostname } from "@/util/paramutil";
import { checkDebug, Debug } from "@/util/debug";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { Browser } from "@capacitor/browser";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { useContext, useEffect } from "react";

import Icon from "@/components/common/icon/icon";
import { cn } from "@/lib/utils";
import { useAppleSignIn } from "@/util/apputil";
import { checkEmpty } from "@/util/stringutil";
import { loginFailed } from "./signin";
import { AlertProps, TransitionType } from "../layoutType";
import { AlertContext } from "@/components/context/alertContext";
import { alertLoading } from "../list/defaultType";
// import dynamic from "next/dynamic";
// const Icon = dynamic(() => import("@/components/common/icon/icon"), { ssr: false });
export const appleLoginCommon = (params: {
  ta: TransitionType;
  alert_p: ((prop: AlertProps) => void) | undefined;
  platform: string;
  url: string;
  loginFn: Function;
  code: string;
  idToken: string;
  firstName: string;
  lastName: string;
}) => {
  const userName = params.firstName + (checkEmpty(params.firstName) && checkEmpty(params.lastName) ? "" : " ") + params.lastName;
  fetchUtil(
    params.url,
    {
      mode: "cors",
      method: "POST",
      headers: { "Content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        code: params.code,
        platform: params.platform,
        id_token: params.idToken,
        userName,
      }),
    },
    undefined,
    (data: any) => {
      Debug(data);
      params.loginFn(data);
      try {
        if (data?.result?.idToken != null) {
          Debug(jwt.decode(data.result.idToken));
        } else {
          Debug("idToken is null");
        }
      } catch (e) {
        Debug("idToken is null");
      }
    },
    (error: any) => {
      loginFailed(params.ta, params.alert_p);
    },
  );
};
export const appleUrlPath = "/api/sign/callback/apple";
export default function AppleLoginButton({ loginFn }: { loginFn: Function }) {
  const ta = useTranslations("alert");
  // const a: UseGoogleLoginOptionsAuthCodeFlow = {};
  // const ux_mode = "redirect";//"popup"
  // const ux_mode = "popup";
  // const urlPath = "/api/sign/callback/apple";
  const redirectURI = `https://${typeof window !== "undefined" ? window.location.hostname : "localhost"}${appleUrlPath}`;
  const url = getApiHostname() + appleUrlPath;
  const auth_init = {
    clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID!, // Service ID
    scope: "name email",
    redirectURI: redirectURI,
    usePopup: true, // popup 방식이면 redirect 없이 바로 응답 받음
  };
  const alertContext = useContext(AlertContext);
  const alert_p = alertContext.setProp ?? ((prop: AlertProps) => {});

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      const script = document.createElement("script");
      script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        if (typeof window !== "undefined") {
          window?.AppleID?.auth?.init(auth_init);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appleLogin = async () => {
    try {
      Debug(auth_init);
      alert_p(alertLoading);
      const response = await window?.AppleID.auth.signIn();
      const result = response.authorization;
      Debug("Apple login success:", response);
      const firstName = response.user?.name?.firstName || "";
      const lastName = response.user?.name?.lastName || "";
      const userName = firstName + (checkEmpty(firstName) && checkEmpty(lastName) ? "" : " ") + lastName;
      appleLoginCommon({ ta, alert_p, platform: "web", url, loginFn, code: result.code, idToken: result.id_token, firstName, lastName });

      // response.authorization.id_token 등을 서버로 전달 → 검증 후 JWT 발급
    } catch (err) {
      loginFailed(ta, alert_p);
      Debug("Apple login error:", err);
    }
  };

  const appLogin = async () => {
    if (Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "web") {
      SignInWithApple.authorize({
        scopes: "FULL_NAME EMAIL",
        clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID!, // Service ID
        redirectURI: redirectURI,
      })
        .then((data) => {
          alert_p(alertLoading);
          const result = data.response;
          const firstName = result.givenName || "";
          const lastName = result.familyName || "";
          appleLoginCommon({
            ta,
            alert_p,
            platform: Capacitor.getPlatform(),
            url,
            loginFn,
            code: result.authorizationCode,
            idToken: result.identityToken,
            firstName,
            lastName,
          });
        })
        .catch((err) => {
          Debug("Apple login error:", err);
        });
    } else {
      await Browser.open({
        url:
          "https://appleid.apple.com/auth/authorize?" +
          "response_type=code%20id_token" +
          "&client_id=" +
          process.env.NEXT_PUBLIC_APPLE_CLIENT_ID! +
          "&redirect_uri=" +
          url +
          "/web" +
          "&scope=name%20email" +
          "&response_mode=form_post", //query
      });
    }
  };
  //const a : GoogleLoginProps;
  //a.context
  return (
    <>
      <Button
        variant="solid"
        type="button"
        color="gray"
        size="3"
        className={cn("p-3 cursor-pointer my-6 mx-[13px]", useAppleSignIn ? "" : "hidden")}
        onClick={Capacitor.getPlatform() === "web" ? appleLogin : appLogin}
      >
        <Icon iconType="phosphor" iconName="AppleLogo" weight="bold" />
        <Text weight="bold">{ta("signin_apple")}</Text>
      </Button>
    </>
  );
}
